import { Toast } from "@techzyapp/ui-lib";
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  HttpStatusCode,
} from "axios";
import { useAuth } from "stores/auth";
import {
  authLocalStorage,
  getRefreshTokenFromLocalStorage,
  getTokenFromLocalStorage,
} from "utils/helpers/auth";

interface ApiConfig extends AxiosRequestConfig {
  baseURL: string;
}

type ApiBaseResponseWithData<TData> = {
  response: TData | null;
  totalCount: number | null;
} & ApiBaseResponse;

export type ApiBaseResponse = {
  isError: boolean;
  errorMessage: string | null;
};

type Subscriber = { resolve: () => void; reject: () => void };

// `silent` skips the global error and success toasts, for stores that show their own
type RequestOptions = { silent?: boolean };

let isRefreshing = false;
let refreshSubscribers: Subscriber[] = [];

const defaultApiUrl =
  "https://cocacolaloyaltycmstest-dzayc8fma5c3bhbt.westeurope-01.azurewebsites.net/";
const API_URL = process.env.REACT_APP_API_URL ?? defaultApiUrl;
const LOYALTY_API_URL = process.env.REACT_APP_LOYALTY_ANALYTICS_API_URL ?? "";

function createApiClient(config: ApiConfig): AxiosInstance {
  const axiosInstance = axios.create(config);

  // Interceptor for adding the authorization header
  axiosInstance.interceptors.request.use((config) => {
    const token = getTokenFromLocalStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (res) => {
      return res;
    },
    async (err) => {
      // retry functionality
      if (
        !!err.config?.retryCount &&
        err.config?.retryCodes?.includes(err.response?.status)
      ) {
        err.config.retryCount -= 1;
        return axiosInstance(err.config);
      }

      // if (!err.response?.data?.infoMessage && err?.response?.status !== 401) {
      //   Toast.error(`Exception - ${err.message}`);
      // }

      // Check if the response status code is 401 (unauthorized) and the error message indicates token expiration
      const originalRequest = err.config;
      const keepMe = true;

      if (err.response.status === HttpStatusCode.Unauthorized && keepMe) {
        const retryOriginalRequest = new Promise((resolve) => {
          const resolveSubscriber = () => {
            resolve(axiosInstance(originalRequest));
          };
          const rejectSubscriber = () => {
            resolve(null);
          };
          refreshSubscribers.push({
            resolve: resolveSubscriber,
            reject: rejectSubscriber,
          });
        });

        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const refreshToken = getRefreshTokenFromLocalStorage();
            // Make a request to the server to refresh the access token using the refresh token
            const response = await axios.post(
              process.env.REACT_APP_API_URL + "api/identity/RefreshToken",
              { refreshToken },
              {
                baseURL: process.env.IDENTITY_URL,
                headers: {
                  Authorization: `Bearer ${getTokenFromLocalStorage()}`,
                },
              }
            );

            const newAccessToken = response.data.response.accessToken;
            const newRefreshToken = response.data.response.refreshToken;

            // Update the access token in the Axios instance and local storage
            axiosInstance.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${newAccessToken}`;
            authLocalStorage({
              token: newAccessToken,
              refreshToken: newRefreshToken,
              keepMe: true,
            });

            // Execute all the pending requests
            refreshSubscribers.forEach((subscriber) => subscriber.resolve());

            refreshSubscribers = [];
          } catch (refreshError) {
            // Handle the refresh token request error, e.g., log out the user
            useAuth.getState().logout();

            refreshSubscribers.forEach((subscriber: Subscriber) =>
              subscriber.reject()
            );
            refreshSubscribers = [];
          } finally {
            isRefreshing = false;
          }
        }
        return retryOriginalRequest;
      }

      // if (!keepMe && err?.response?.status === HttpStatusCode.Unauthorized) {
      //   if (isClient()) {
      //     toasts.error('Session Expired', { toastId: 'toast-custom-id' })
      //     logoutEvent()
      //   }
      //   setAuthorizationToken('')
      // }

      return Promise.reject(err);
    }
  );

  // ... (Rest of the interceptor code)
  return axiosInstance;
}

function showApiError(ex: unknown | AxiosError, options?: RequestOptions) {
  let errorMessage = "something went wrong";

  if (axios.isAxiosError(ex)) {
    const data = ex.response?.data;
    // ASP.NET model-validation 400s carry no errorMessage, only `errors`/`title`.
    errorMessage =
      data?.ErrorMessage ||
      data?.errorMessage ||
      (data?.errors
        ? Object.values(data.errors).flat().join(",")
        : data?.title) ||
      errorMessage;
    if (errorMessage.includes(",")) {
      errorMessage = errorMessage
        ?.split(",")
        ?.map((o: string, i: number) => `${i + 1}.${o}`)
        .join("\r\n");
    }
  }

  if (axios.isAxiosError(ex) && !ex.response?.data?.response) {
    // TODO error dialog
  }

  // `silent` callers show their own toast
  if (!options?.silent) Toast.error(errorMessage);
}

class ApiManager {
  private apiClient: AxiosInstance;

  constructor(apiUrl: string) {
    const defaultConfig: ApiConfig = { baseURL: apiUrl };

    this.apiClient = createApiClient(defaultConfig);
  }

  async getData<T>(
    url: string,
    params?: AxiosRequestConfig,
    options?: RequestOptions
  ) {
    try {
      const response = await this.apiClient.get<ApiBaseResponseWithData<T>>(
        url,
        params
      );

      return {
        isError: false,
        errorMessage: null,
        response: response.data.response,
        totalCount: response.data.totalCount,
      };
    } catch (ex: unknown | AxiosError) {
      showApiError(ex, options);
      const apiError = ex as AxiosError<any>;
      return {
        isError: true,
        errorMessage:
          apiError.response?.data?.ErrorMessage ||
          apiError.response?.data?.errorMessage ||
          (apiError.response?.data?.errors
            ? Object.values(apiError.response.data.errors).flat().join(",")
            : apiError.response?.data?.title) ||
          "Something went wrong",
      };
    }
  }

  async postData<T>(url: string, data?: any, options?: RequestOptions) {
    try {
      const response = await this.apiClient.post<ApiBaseResponseWithData<T>>(
        url,
        data
      );
      // Suppress generic success toast for chatbot ask endpoint to avoid noisy UX
      if (!options?.silent && !/api\/AI\/chatbot\/ask$/i.test(url)) {
        Toast.success("Success");
      }

      return {
        isError: false,
        errorCode: null,
        response: response.data.response,
      };
    } catch (ex: unknown | AxiosError) {
      showApiError(ex, options);
      const apiError = ex as AxiosError<any>;
      return {
        isError: true,
        errorMessage:
          apiError.response?.data?.ErrorMessage ||
          apiError.response?.data?.errorMessage ||
          (apiError.response?.data?.errors
            ? Object.values(apiError.response.data.errors).flat().join(",")
            : apiError.response?.data?.title) ||
          "Something went wrong",
      };
    }
  }

  async putData<T>(url: string, data?: any, options?: RequestOptions) {
    try {
      const response = await this.apiClient.put<ApiBaseResponseWithData<T>>(
        url,
        data
      );
      if (!options?.silent) Toast.success("Success");

      return {
        isError: false,
        errorCode: null,
        response: response.data.response,
      };
    } catch (ex: unknown | AxiosError) {
      showApiError(ex, options);
      const apiError = ex as AxiosError<any>;
      return {
        isError: true,
        errorMessage:
          apiError.response?.data?.ErrorMessage ||
          apiError.response?.data?.errorMessage ||
          (apiError.response?.data?.errors
            ? Object.values(apiError.response.data.errors).flat().join(",")
            : apiError.response?.data?.title) ||
          "Something went wrong",
      };
    }
  }

  async deleteData<T>(url: string, options?: RequestOptions) {
    try {
      const response = await this.apiClient.delete<ApiBaseResponseWithData<T>>(url);
      if (!options?.silent) Toast.success("Success");

      return {
        isError: false,
        errorCode: null,
        response: response.data.response,
      };
    } catch (ex: unknown | AxiosError) {
      showApiError(ex, options);
      const apiError = ex as AxiosError<any>;
      return {
        isError: true,
        errorMessage:
          apiError.response?.data?.ErrorMessage ||
          apiError.response?.data?.errorMessage ||
          (apiError.response?.data?.errors
            ? Object.values(apiError.response.data.errors).flat().join(",")
            : apiError.response?.data?.title) ||
          "Something went wrong",
      };
    }
  }
}

const apiManager = new ApiManager(API_URL);
export const loyaltyApimanager = new ApiManager(LOYALTY_API_URL);

export default apiManager;
